import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button as MuiButton,
  Chip,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  Add as CreateIcon,
  Notifications as NotificationsIcon,
  CheckCircle,
  Schedule,
  Error,
  Edit as EditIcon,
  AutoMode,
  OpenInNew,
} from '@mui/icons-material';
import {
  useRecordContext,
  useGetList,
  useNotify,
  useRefresh,
} from 'react-admin';
import { ReminderDialog } from './dialogs/ReminderDialog';
import { tenantService } from '../services/tenantService';

interface Reminder {
  id: number;
  notes?: string; // description
  reminderDate: string;
  status: string;
  reminderType?: {
    id: number;
    name: string;
    color: string;
    icon?: string;
  };
  responsibleUser?: {
    id: number;
    name: string;
  };
  // Auto-generated reminder fields
  sourceEntityType?: string;
  sourceEntityId?: number;
  expirationDate?: string;
  daysBeforeExpiry?: number;
  autoGenerated?: boolean;
}

const StatusChip = ({ reminder }: { reminder: Reminder }) => {
  const getStatusProps = (status: string, dueDate?: string) => {
    const now = new Date();
    const due = dueDate ? new Date(dueDate) : null;
    const isOverdue = due && due < now;

    switch (status?.toLowerCase()) {
      case 'completed':
        return {
          label: 'Completed',
          color: '#4caf50' as const,
          icon: <CheckCircle fontSize="small" />,
        };
      case 'pending':
        if (isOverdue) {
          return {
            label: 'Overdue',
            color: '#f44336' as const,
            icon: <Error fontSize="small" />,
          };
        }
        return {
          label: 'Pending',
          color: '#2196f3' as const,
          icon: <Schedule fontSize="small" />,
        };
      default:
        return {
          label: status || 'Unknown',
          color: '#757575' as const,
          icon: <Schedule fontSize="small" />,
        };
    }
  };

  const statusProps = getStatusProps(reminder.status, reminder.reminderDate);

  return (
    <Chip
      label={statusProps.label}
      icon={statusProps.icon}
      sx={{
        backgroundColor: `${statusProps.color}20`,
        color: statusProps.color,
        borderColor: `${statusProps.color}40`,
        border: '1px solid',
      }}
      size="small"
    />
  );
};


const ReminderRow = ({ reminder, onEdit, context = 'equipment' }: { reminder: Reminder; onEdit: (id: number) => void; context?: string }) => {
  const getSourceEntityLink = () => {
    if (!reminder.sourceEntityType || !reminder.sourceEntityId) return null;

    const entityTypeMap: Record<string, string> = {
      'DriverLicense': 'drivers',
      'DriverCertification': 'drivers',
      'DriverPassport': 'drivers',
      'EquipmentDocument': 'equipment',
      'EquipmentInspection': 'equipment',
    };

    const baseRoute = entityTypeMap[reminder.sourceEntityType];
    if (!baseRoute) return null;

    // For driver-related items, we need to get the driver ID from the backend data
    // For equipment-related items, we can use the equipment ID
    // This is a simplified version - you might need to adjust based on your data structure
    return baseRoute === 'equipment' ? `/${baseRoute}/${reminder.sourceEntityId}` : null;
  };

  const sourceLink = getSourceEntityLink();

  return (
    <TableRow hover>
      <TableCell>
        <Box>
          <Typography variant="body2">
            {reminder.reminderDate ? new Date(reminder.reminderDate).toLocaleDateString() : '-'}
          </Typography>
          {reminder.expirationDate && (
            <Tooltip title={`Actual expiration date: ${new Date(reminder.expirationDate).toLocaleDateString()}`}>
              <Typography variant="caption" color="text.secondary">
                Expires: {new Date(reminder.expirationDate).toLocaleDateString()}
              </Typography>
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: reminder.reminderType?.color || '#757575' }} />
            {reminder.reminderType?.name || 'Unknown'}
          </Box>
          {reminder.autoGenerated && (
            <Tooltip title="Automatically generated based on expiration dates">
              <Chip
                icon={<AutoMode sx={{ fontSize: 14 }} />}
                label="Auto"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  backgroundColor: '#e3f2fd',
                  color: '#1976d2',
                }}
              />
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Box>
          <Typography variant="body2">{reminder.notes || '-'}</Typography>
          {sourceLink && (
            <Tooltip title="View source item">
              <Link to={sourceLink} style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                <Typography variant="caption" color="primary">
                  View {reminder.sourceEntityType}
                </Typography>
                <OpenInNew sx={{ fontSize: 12 }} color="primary" />
              </Link>
            </Tooltip>
          )}
        </Box>
      </TableCell>
      {context === 'equipment' && <TableCell>{reminder.responsibleUser?.name || '-'}</TableCell>}
      <TableCell><StatusChip reminder={reminder} /></TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" onClick={() => onEdit(reminder.id)} color="primary">
            <EditIcon />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );
};

interface RemindersTabProps {
  context?: 'equipment' | 'user';
}

export const RemindersTab = ({ context = 'equipment' }: RemindersTabProps) => {
  const record = useRecordContext();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRecordId, setEditRecordId] = useState<number | null>(null);

  // Choose filter based on context
  // For user context, filter by userId (reminders ABOUT this user)
  // For equipment context, filter by vehicleId (reminders for this equipment)
  const filter = context === 'user'
    ? { userId: record?.id }
    : { vehicleId: record?.id };

  const { data: reminders, isLoading, refetch } = useGetList('reminders', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'reminderDate', order: 'DESC' },
    filter,
  });

  // Sort on client side as backup if server sorting doesn't work
  const sortedReminders = reminders?.sort((a, b) => {
    const dateA = new Date(a.reminderDate);
    const dateB = new Date(b.reminderDate);
    return dateB.getTime() - dateA.getTime(); // DESC order (newest first)
  });

  const handleEdit = (id: number) => {
    setEditRecordId(id);
    setEditDialogOpen(true);
  };

  const handleCreate = () => {
    setEditRecordId(null);
    setCreateDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>Loading reminders...</Typography>
      </Box>
    );
  }

  const contextLabels = {
    equipment: {
      title: 'Equipment Reminders',
      emptyMessage: 'No reminders set for this equipment',
      emptyDescription: 'Create reminders for maintenance, inspections, and other important tasks'
    },
    user: {
      title: 'User Reminders',
      emptyMessage: 'No reminders set for this user',
      emptyDescription: 'Create reminders for training, certifications, and other important tasks'
    }
  };

  const labels = contextLabels[context];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" color="primary">
          {labels.title}
        </Typography>
        <MuiButton
          onClick={handleCreate}
          startIcon={<CreateIcon />}
          variant="contained"
          size="small"
        >
          Add Reminder
        </MuiButton>
      </Box>

      <ReminderDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        equipmentId={context === 'equipment' ? record?.id : undefined}
        userId={context === 'user' ? record?.id : undefined}
      />

      <ReminderDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        equipmentId={context === 'equipment' ? record?.id : undefined}
        userId={context === 'user' ? record?.id : undefined}
        recordId={editRecordId}
        isEdit={true}
      />

      {sortedReminders?.length ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Due Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              {context === 'equipment' && <TableCell>Assigned To</TableCell>}
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedReminders.map((reminder: Reminder) => (
              <ReminderRow key={reminder.id} reminder={reminder} onEdit={handleEdit} context={context} />
            ))}
          </TableBody>
        </Table>
      ) : (
        <Box sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: '#fafafa',
          borderRadius: 2,
          border: '1px solid #e0e0e0'
        }}>
          <NotificationsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {labels.emptyMessage}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {labels.emptyDescription}
          </Typography>
          <MuiButton
            onClick={handleCreate}
            startIcon={<CreateIcon />}
            variant="contained"
            size="small"
          >
            Add First Reminder
          </MuiButton>
        </Box>
      )}
    </Box>
  );
};